import { Bot, ChevronLeft, ChevronRight, Loader2, MessageSquare, Mic, Send, Square, User } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../utils/api";
import { ChatBotProps } from "./types";
import useVoiceRecording from "./useVoiceRecording";



const ChatBot: React.FC<ChatBotProps> = ({ isInitialOpen, messages, setMessage, projectId, sectionId, isDisabled }) => {
  const { i18n } = useTranslation();
  const [inputValue, setInputValue] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(isInitialOpen);

  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value);
  };

  // Helper function to convert messages to OpenAI format
  const convertMessagesToHistory = (messages: any[]) => {
    return messages
      .filter(msg => !msg.isError) // Exclude error messages
      .map(msg => ({
        role: msg.sender === "user" ? "user" : "assistant",
        content: typeof msg.text === "string" ? msg.text : "" // Only include text content, not JSX
      }))
      .filter(msg => msg.content.trim().length > 0); // Exclude empty messages
  };

  // Handle transcription from voice recording hook
  async function handleTranscription(audioBlob: Blob): Promise<void> {
    try {
      // Get current language from i18next
      const currentLanguage = i18n.language || "de";
      const response = await api.transcribeAudio(audioBlob, currentLanguage);

      if (response.success && response.transcript) {
        const transcript = response.transcript.trim();
        if (transcript) {
          // Add user message to chat immediately
          setMessage({ sender: "user", text: transcript });

          // Clear any existing input and errors
          setInputValue("");
          setError(null);
          setVoiceError(null);

          // Send the transcribed message to the chatbot with conversation history
          setIsLoading(true);
          try {
            const conversationHistory = convertMessagesToHistory(messages);
            const historyToSend = conversationHistory.length > 0 ? conversationHistory : null;
            const chatResponse = await api.aiChat(
              transcript,
              projectId as any,
              sectionId as any,
              historyToSend as any
            );

            console.log('🔍 ChatBot (voice) received API response:', chatResponse);

            // The new API structure returns objectIds directly, not in the answer
            // Pass the full response to the message handler
            setMessage({
              sender: "bot",
              text: chatResponse.answer,
              objectIds: chatResponse.objectIds || [],
              totalObjects: chatResponse.totalObjects || 0,
              appliedFilters: chatResponse.appliedFilters || {},
              filterDescription: chatResponse.filterDescription || ""
            });
          } catch (chatErr: any) {
            const errorMessage = chatErr.message || "Entschuldigung, ich konnte keine Antwort erhalten.";
            setError(errorMessage);
            // Add error message to chat
            setMessage({ sender: "bot", text: `Fehler: ${chatErr.message || "Anfrage fehlgeschlagen."}`, isError: true });
          } finally {
            setIsLoading(false);
          }
        }
      }
    } catch (err) {
      const errorMessage = "Spracherkennung fehlgeschlagen. Bitte versuchen Sie es erneut.";
      setError(errorMessage);
      throw err; // Re-throw so the hook can handle it
    }
  }

  // Voice recording hook (initialized after handleTranscription is defined)
  const {
    isRecording,
    isTranscribing,
    isListening,
    audioPermission,
    error: voiceError,
    toggleRecording,
    setError: setVoiceError
  } = useVoiceRecording({
    onTranscription: handleTranscription,
    silenceThreshold: 30,
    silenceDuration: 1500,
    minRecordingTime: 500
  });

  useEffect(scrollToBottom, [messages]);

  // Combine errors from component and voice recording hook
  useEffect(() => {
    if (voiceError) {
      setError(voiceError);
    }
  }, [voiceError]);

  const handleSendMessage = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const userMessage = inputValue.trim();
    if (!userMessage || isLoading) return;

    // Add user message to chat
    setMessage({ sender: "user", text: userMessage });
    setInputValue("");
    setIsLoading(true);
    setError(null);

    try {
      // Convert current messages to conversation history
      const conversationHistory = convertMessagesToHistory(messages);
      const historyToSend = conversationHistory.length > 0 ? conversationHistory : null;

      // Call the API endpoint with conversation history
      const response = await api.aiChat(
        userMessage,
        projectId as any,
        sectionId as any,
        historyToSend as any
      );

      console.log('🔍 ChatBot received API response:', response);

      // The new API structure returns objectIds directly, not in the answer
      // Pass the full response to the message handler - it contains:
      // { answer, objectIds, totalObjects, appliedFilters, filterDescription }
      setMessage({
        sender: "bot",
        text: response.answer,
        objectIds: response.objectIds || [],
        totalObjects: response.totalObjects || 0,
        appliedFilters: response.appliedFilters || {},
        filterDescription: response.filterDescription || ""
      });
    } catch (err: any) {
      setError(
        err.message || "Entschuldigung, ich konnte keine Antwort erhalten."
      );
      // Optionally add an error message to the chat
      setMessage({ sender: "bot", text: `Fehler: ${err.message || "Anfrage fehlgeschlagen."}`, isError: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrophoneClick = (): void => {
    toggleRecording();
  };

  return (
    isOpen ? (
      <div className="h-full w-[320px] bg-white flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-3 border-b rounded-t-lg">
          <h3 className="text-sm font-semibold text-gray-700">Chat Assistant</h3>
          <button onClick={() => setIsOpen(false)} className="text-gray-500 hover:text-gray-700">
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex items-start space-x-2 ${msg.sender === "user" ? "justify-end" : ""
                }`}
            >
              {msg.sender === "bot" && (
                <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white">
                  <Bot size={14} />
                </div>
              )}
              <div
                className={`max-w-[80%] p-2 rounded-lg text-sm ${msg.sender === "user"
                  ? "bg-blue-500 text-white"
                  : msg.isError
                    ? "bg-red-100 text-red-700"
                    : "bg-gray-200 text-gray-800"
                  }`}
              >
                {msg.text}
              </div>
              {msg.sender === "user" && (
                <div className="flex-shrink-0 w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center text-gray-600">
                  <User size={14} />
                </div>
              )}
            </div>
          ))}
          {(isLoading || isTranscribing) && (
            <div className="flex items-start space-x-2">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white">
                <Bot size={14} />
              </div>
              <div className="bg-gray-200 text-gray-800 p-2 rounded-lg flex items-center space-x-2">
                <Loader2 size={16} className="animate-spin" />
                <span className="text-xs">
                  {isTranscribing ? "Transkribiere..." : "Denke..."}
                </span>
              </div>
            </div>
          )}
          {/* Invisible element to scroll to */}
          <div ref={messagesEndRef} />
        </div>

        {/* Error Display */}
        {error && (
          <div className="px-3 py-1 bg-red-50 border-t border-red-200">
            <p className="text-xs text-red-600">{error}</p>
          </div>
        )}

        {/* Input Area */}
        <form
          onSubmit={handleSendMessage}
          className="p-3 border-t flex items-center space-x-2"
        >
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Ihre Frage..."
            className="flex-1 border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            disabled={isLoading || isTranscribing}
          />

          {/* Microphone Button - always visible; permission requested on click */}
          <button
            type="button"
            onClick={handleMicrophoneClick}
            className={`p-2 rounded-md text-white transition-colors relative ${isRecording
              ? isListening
                ? "bg-red-600 hover:bg-red-700 animate-pulse"
                : "bg-yellow-600 hover:bg-yellow-700"
              : isLoading || isTranscribing || isDisabled
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700"
              }`}
            disabled={isLoading || isTranscribing || isDisabled}
            title={
              isRecording
                ? isListening
                  ? "Aufnahme läuft - spricht gerade"
                  : "Aufnahme läuft - wartet auf Sprache"
                : audioPermission === false
                  ? "Mikrofon blockiert oder nicht verfügbar"
                  : "Sprachaufnahme starten"
            }
          >
            {isRecording ? <Square size={16} /> : <Mic size={16} />}
            {/* Visual indicator for listening state */}
            {isRecording && (
              <div
                className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${isListening ? "bg-green-400 animate-pulse" : "bg-yellow-400"
                  }`}
              />
            )}
          </button>

          <button
            type="submit"
            className={`p-2 rounded-md text-white ${isLoading || isTranscribing || isDisabled
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
              }`}
            disabled={isLoading || isTranscribing || isDisabled}
          >
            <Send size={16} />
          </button>
        </form>
      </div>
    ) : (
      <div className="h-full bg-white flex flex-col p-3">
        <button onClick={() => setIsOpen(true)} className="text-gray-500 hover:text-gray-700">
          <ChevronLeft size={20} />
        </button>
        <div className="flex-1 flex items-center">
          <MessageSquare size={20} />
        </div>
      </div>
    )
  );
};

export default ChatBot;
